import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { categories } from '@/mocks/categories';
import { movies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';

const heroBackground = 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20cinema%20interior%20with%20warm%20red%20and%20gold%20curtains%20flowing%20in%20dramatic%20lighting%2C%20elegant%20theater%20atmosphere%2C%20soft%20bokeh%20lights%2C%20luxurious%20textures%2C%20cinematic%20composition%2C%20dark%20rich%20background%20with%20warm%20accents&width=1800&height=1000&seq=hero-cinema-bg&orientation=landscape';

const featuredMovie = movies.find((m) => m.id === 'mv-006')!;
const hotMovies = movies.filter((m) => m.isHot).slice(0, 6);
const newMovies = movies.filter((m) => m.isNew).slice(0, 6);
const featuredArticles = articles.slice(0, 3);

export default function Home() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黄瓜影院',
    url: 'https://huanggua.tv',
    description: '黄瓜影院提供免费电视剧全集、最新剧集完整版，高清在线观看不缓冲流畅播放。海量片库，即点即播，智能推荐追剧。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://huanggua.tv/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄瓜影院',
    url: 'https://huanggua.tv',
    description: '免费高清在线影视平台',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜影院的影视资源需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜影院所有影视资源完全免费在线观看，无需任何付费或会员注册。我们致力于为用户提供免费的优质观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '观看时缓冲卡顿怎么办？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜影院采用先进的CDN加速技术，支持多线路自动切换。如果遇到缓冲问题，建议检查网络连接或切换到较低清晰度，一般情况下可以实现流畅播放不缓冲。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜影院的影视资源更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持每日更新，第一时间上线最新电影、电视剧、动漫和综艺节目。热门剧集通常在播出后数小时内即可在黄瓜影院观看。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜影院支持全平台观看，包括电脑端、手机浏览器、平板等设备。只要有网络连接，随时随地都能享受高清流畅的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="黄瓜影院-免费电视剧全集最新剧集完整版，高清影视在线观看不缓冲流畅播放"
        description="黄瓜影院🚨免费电视剧全集，涵盖最新剧集完整版，高清在线观看不缓冲流畅播放。🍿电影大全海量片库，高清画质即点即播，快速找片智能推荐，满足您的追剧需求。"
        keywords="免费电视剧,电视剧全集,最新剧集,完整版,高清在线观看,不缓冲,电影大全,流畅播放,追剧,快速找片,智能推荐,黄瓜影院"
        canonicalUrl="https://huanggua.tv"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBackground}
              alt="黄瓜影院-高清影视在线观看平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-background-50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 max-w-3xl leading-tight">
              海量高清影视，<span className="text-accent-400">免费在线观看</span>
            </h1>
            <p className="text-sm md:text-base text-background-200 max-w-xl mb-8 leading-relaxed">
              热门电影、最新电视剧、精彩动漫、爆笑综艺，高清画质即点即播，流畅不缓冲。<strong>黄瓜影院</strong>，满足您的所有追剧需求。
            </p>
            <div className="flex items-center gap-3 flex-col sm:flex-row">
              <Link
                to="/category/movie"
                className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                立即观影 <i className="ri-arrow-right-line ml-1.5"></i>
              </Link>
              <Link
                to="/category/tv"
                className="px-6 py-3 text-sm font-semibold bg-background-50/15 text-background-50 border border-background-50/30 rounded-lg hover:bg-background-50/25 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                热门电视剧 <i className="ri-tv-2-line ml-1.5"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 -mt-10 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-500 text-center leading-snug">{cat.description}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Content Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  热门推荐 <span className="text-primary-500">🔥</span>
                </h2>
                <p className="text-sm text-foreground-500 mt-2">全网热播，大家都在看</p>
              </div>
              <Link
                to="/category/movie"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Content Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  最新上线 <span className="text-accent-500">🆕</span>
                </h2>
                <p className="text-sm text-foreground-500 mt-2">第一时间更新，精彩不容错过</p>
              </div>
              <Link
                to="/category/tv"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Content Banner */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <Link
              to={`/article/${featuredMovie.id}`}
              className="group relative block rounded-2xl overflow-hidden h-[280px] md:h-[400px] cursor-pointer"
            >
              <img
                src={featuredMovie.image}
                alt={featuredMovie.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/40 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12 max-w-lg">
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-accent-500 text-background-50 rounded-full w-fit mb-3 whitespace-nowrap">
                  <i className="ri-fire-fill"></i> 热播爆款
                </span>
                <h2 className="font-heading text-2xl md:text-4xl text-background-50 mb-3">{featuredMovie.title}</h2>
                <p className="text-sm text-background-200 line-clamp-2 mb-4">{featuredMovie.description}</p>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-sm text-accent-400">
                    <i className="ri-star-fill"></i> {featuredMovie.rating}
                  </span>
                  <span className="text-sm text-background-300">{featuredMovie.year}</span>
                  <span className="text-sm text-background-300">{featuredMovie.duration}</span>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Articles Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  影视资讯
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最新影视动态、深度剧评影评</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于黄瓜影院，你想知道的都在这里</p>
            </div>
            <div className="space-y-3">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  <a href="#faq-1" className="hover:text-primary-600 transition-colors cursor-pointer">
                    黄瓜影院的影视资源需要付费吗？
                  </a>
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  黄瓜影院所有影视资源完全<strong>免费在线观看</strong>，无需任何付费或会员注册。我们致力于为用户提供免费的优质观影体验。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  <a href="#faq-2" className="hover:text-primary-600 transition-colors cursor-pointer">
                    观看时缓冲卡顿怎么办？
                  </a>
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  黄瓜影院采用先进的CDN加速技术，支持多线路自动切换。如果遇到缓冲问题，建议检查网络连接或切换到较低清晰度，一般情况下可以实现<strong>流畅播放不缓冲</strong>。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  <a href="#faq-3" className="hover:text-primary-600 transition-colors cursor-pointer">
                    影视资源更新频率如何？
                  </a>
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们保持<strong>每日更新</strong>，第一时间上线最新电影、电视剧、动漫和综艺节目。热门剧集通常在播出后数小时内即可在黄瓜影院观看。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  <a href="#faq-4" className="hover:text-primary-600 transition-colors cursor-pointer">
                    黄瓜影院支持哪些设备观看？
                  </a>
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  黄瓜影院支持<strong>全平台观看</strong>，包括电脑端、手机浏览器、平板等设备。只要有网络连接，随时随地都能享受高清流畅的观影体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-primary-600">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">
              开始你的追剧之旅
            </h2>
            <p className="text-sm text-background-200 mb-8 max-w-lg mx-auto">
              海量高清影视资源，免费在线观看。无论你喜欢什么类型的影片，黄瓜影院都能满足你的需求。
            </p>
            <div className="flex items-center justify-center gap-3 flex-col sm:flex-row">
              <Link
                to="/category/movie"
                className="px-6 py-3 text-sm font-semibold bg-background-50 text-primary-600 rounded-lg hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                浏览电影大全
              </Link>
              <Link
                to="/category/tv"
                className="px-6 py-3 text-sm font-semibold bg-transparent text-background-50 border border-background-50/40 rounded-lg hover:bg-background-50/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                热门电视剧
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}